import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Lock, Loader2, Info } from 'lucide-react';
import { puertoRicoCities, unitedStatesStates } from '../data/locations';
declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    __leadEnviado?: boolean;
  }
}
const CALENDLY_URL = 'https://calendly.com/vantixcredit/diagnostico-de-credito';
// Lead delivery endpoint.
// Using the raw email forces FormSubmit to re-send the activation email
// whenever the page is served from a new origin. Replace this value with the
// permanent alias token from the FormSubmit activation email
// (e.g. 'a1b2c3d4e5f6...') to keep delivery active forever.
const FORMSUBMIT_TARGET = 'eduardoar13@gmail.com';
type Step = {
  id: string;
  title: string;
  // Optional lead-in paragraph shown above the question
  context?: string;
  subtitle?: string;
  type: 'single' | 'multi';
  options: string[];
};
const steps: Step[] = [{
  id: 'negatives',
  title: '¿Qué cuentas negativas tiene tu crédito?',
  subtitle: 'Selecciona todo lo que aplique. No te preocupes, lo arreglamos.',
  type: 'multi',
  options: ['Colecciones', 'Pagos tarde', 'Charge off', 'Hard inquiries', 'Reposición de vehículo', 'No lo tengo claro']
}, {
  id: 'goals',
  title: '¿Para qué quieres reparar tu crédito?',
  subtitle: 'Selecciona todas las que apliquen para darte el mejor plan.',
  type: 'multi',
  options: ['🏡  Comprar una casa', '🚗  Comprar un carro', '💼  Capital para mi negocio', '💵  Buscar un préstamo personal']
}, {
  id: 'priority',
  title: '¿Qué tan importante es reparar tu crédito para ti ahora mismo?',
  type: 'single',
  options: ['Es importante solucionarlo, se ha vuelto mi prioridad', 'Solo estoy explorando opciones']
}, {
  id: 'attempts',
  title: '¿Has intentado reparar tu crédito antes por tu cuenta o con otra compañía?',
  type: 'single',
  options: ['Lo intenté por mí mismo, pero no lo logré', 'Lo intenté con otra compañía pero sin resultados', 'Ninguna de las anteriores']
}, {
  id: 'budget',
  context: 'La sesión de consulta es gratuita, pero los servicios de Vantix tienen un valor de $499.99 USD en adelante. Si en la sesión de consultoría encontramos la manera de ayudarte y entregarte los resultados que buscas…',
  title: '¿Cuentas con este mínimo de inversión?',
  type: 'single',
  options: ['Sí, me lo puedo permitir', 'No, no cuento con el monto mínimo']
}];
const TOTAL_STEPS = steps.length + 1;

// Answer that ends the flow: we only continue with people who can invest.
const BUDGET_STEP_ID = 'budget';
const DISQUALIFYING_ANSWER = 'No, no cuento con el monto mínimo';
type Contact = {
  name: string;
  phone: string;
  email: string;
  country: string;
  city: string;
};
const emptyContact: Contact = {
  name: '',
  phone: '',
  email: '',
  country: '',
  city: ''
};
export function QualificationForm() {
  const [step, setStep] = useState(0);
  const [single, setSingle] = useState<Record<string, string>>({});
  const [multi, setMulti] = useState<Record<string, string[]>>({});
  const [contact, setContact] = useState<Contact>(emptyContact);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [disqualified, setDisqualified] = useState(false);
  const isContactStep = step === steps.length;
  const current = steps[step];
  const progress = Math.round((submitted ? TOTAL_STEPS : step) / TOTAL_STEPS * 100);
  const cityOptions = contact.country === 'Puerto Rico' ? puertoRicoCities : contact.country === 'Estados Unidos' ? unitedStatesStates : [];
  const handleSingle = (id: string, option: string) => {
    setSingle(prev => ({
      ...prev,
      [id]: option
    }));
    if (id === BUDGET_STEP_ID && option === DISQUALIFYING_ANSWER) {
      window.setTimeout(() => setDisqualified(true), 220);
      return;
    }
    window.setTimeout(() => setStep(s => s + 1), 220);
  };
  const resetForm = () => {
    setStep(0);
    setSingle({});
    setMulti({});
    setContact(emptyContact);
    setSubmitted(false);
    setDisqualified(false);
  };
  const toggleMulti = (id: string, option: string) => {
    setMulti(prev => {
      const currentList = prev[id] ?? [];
      return {
        ...prev,
        [id]: currentList.includes(option) ? currentList.filter(o => o !== option) : [...currentList, option]
      };
    });
  };
  const contactValid = contact.name.trim().length > 2 && contact.phone.trim().length >= 7 && /\S+@\S+\.\S+/.test(contact.email) && contact.country !== '' && contact.city !== '';
  const labelClass = 'block text-[11px] font-semibold tracking-[0.18em] uppercase text-white/65 mb-2';
  const inputClass = 'w-full rounded-lg border border-white/12 bg-white/[0.03] px-4 py-3 text-[15px] text-white placeholder:text-white/25 font-normal outline-none transition-colors focus:border-gold';

  // Prefill Calendly with what they already told us so they don't retype it.
  const bookingUrl = `${CALENDLY_URL}?name=${encodeURIComponent(contact.name)}&email=${encodeURIComponent(contact.email)}`;
  const sendLead = async () => {
    setSending(true);
    const payload: Record<string, string> = {
      _subject: `Nuevo formulario completado: ${contact.name}`,
      _template: 'box',
      _captcha: 'false',
      Mensaje: `Saludos Eduardo, el cliente ${contact.name} ha llenado el formulario exitosamente, acá está lo que contestó:`,
      'Nombre y apellido': contact.name,
      Teléfono: contact.phone,
      'Correo electrónico': contact.email,
      Ubicación: `${contact.city}, ${contact.country}`
    };
    steps.forEach(s => {
      payload[s.title] = s.type === 'single' ? single[s.id] ?? '—' : (multi[s.id] ?? []).join(', ') || '—';
    });
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${FORMSUBMIT_TARGET}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error('No se pudo entregar el formulario');
      }
      if (typeof window.fbq === 'function' && !window.__leadEnviado) {
        window.__leadEnviado = true;
        window.fbq('track', 'Lead', {
          value: 1500,
          currency: 'USD'
        });
      }
      setSubmitted(true);
      window.setTimeout(() => {
        window.location.href = bookingUrl;
      }, 600);
    } catch {
      // Keep the contact step open so the person can retry the submission.
    } finally {
      setSending(false);
    }
  };
  return <section id="calificar" className="bg-ink-900 pt-14 pb-20 md:py-28 px-4 sm:px-6 lg:px-8 border-t border-white/10" data-id="element-70" data-mp="2h1b331g6wjc8">
      <div className="max-w-3xl mx-auto" data-id="element-71" data-mp="24k8q8mxrm4ql">
        {/* Heading */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center mb-9 md:mb-12" data-id="element-72" data-mp="24krzzgd9hmtj">
          <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-gold mb-5" data-id="element-73" data-mp="1lrxqv32bkox8">
            Evaluación preliminar
          </p>
          <h2 className="text-3xl md:text-[2.75rem] font-semibold text-white tracking-tight mb-4 leading-[1.18]" data-id="element-74" data-mp="1ctvks93qb633">
            Veamos si podemos <span className="text-gold" data-id="element-75" data-mp="17tg4p9s2bkhf">ayudarte</span>
          </h2>
          <p className="text-base md:text-lg text-white/80 font-normal max-w-lg mx-auto leading-relaxed" data-id="element-76" data-mp="12umvvunq0hnl">
            Responde unas preguntas rápidas sobre tu caso. Con eso preparamos tu
            diagnóstico antes de la llamada.
          </p>
        </motion.div>

        {/* Card */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.8,
        delay: 0.1,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-2xl border border-white/10 bg-ink-850 px-5 py-7 sm:p-8 md:p-10" data-id="element-77" data-mp="2hrfq3k895u8v">
          {/* Progress */}
          <div className="mb-8" data-id="element-78" data-mp="2331mv86xc3rs">
            <div className="flex items-center justify-between mb-3" data-id="element-79" data-mp="22zxctu6s2dt7">
              <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/65" data-id="element-80" data-mp="1p3jukiuf68do">
                {submitted ? 'Completado' : disqualified ? 'Evaluación finalizada' : `Paso ${step + 1} de ${TOTAL_STEPS}`}
              </span>
              <span className="text-[11px] font-semibold tracking-wide text-gold" data-id="element-81" data-mp="15i9wem0kdjky">
                {progress}%
              </span>
            </div>
            <div className="h-[3px] w-full rounded-full bg-white/10 overflow-hidden" data-id="element-82" data-mp="1fe3iox434rfx">
              <motion.div className="h-full rounded-full bg-gold" animate={{
              width: `${progress}%`
            }} transition={{
              duration: 0.4,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-83" data-mp="1te4ie98m5stt" />
            </div>
          </div>

          <AnimatePresence mode="wait" data-id="element-84" data-mp="1y6fgr7nph6k9">
            {/* Not a fit: flow stops here */}
            {disqualified && <motion.div key="disqualified" initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.98
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} className="text-center py-4" data-id="element-85" data-mp="2c55hqydnbqdw">
                <div className="h-14 w-14 rounded-full border border-white/15 bg-white/[0.03] flex items-center justify-center mx-auto mb-6" data-id="element-86" data-mp="1y4wh1k5nhbm0">
                  <Info className="h-6 w-6 text-white/60" strokeWidth={1.75} data-id="element-87" data-mp="1t4rxtmdcy2s3" />
                </div>

                <h3 className="text-xl md:text-[1.6rem] font-semibold text-white mb-4 leading-snug" data-id="element-88" data-mp="1o9upyirtdzgs">
                  Gracias por tu interés
                </h3>
                <p className="text-base text-white/85 font-normal leading-relaxed max-w-md mx-auto" data-id="element-89" data-mp="cteaipg9fitx">
                  Lo sentimos, en este momento no cuentas con los requisitos
                  mínimos para comenzar el proceso con Vantix. Nuestros
                  servicios tienen una inversión desde $499.99 USD.
                </p>
                <p className="text-base text-white/85 font-normal leading-relaxed max-w-md mx-auto mt-4" data-id="element-90" data-mp="2odlsw69ejcu4">
                  Cuando estés en condiciones de hacer esta inversión, con gusto
                  te atenderemos.
                </p>

                <button onClick={() => {
              setDisqualified(false);
              setSingle(prev => {
                const next = {
                  ...prev
                };
                delete next[BUDGET_STEP_ID];
                return next;
              });
            }} className="mt-8 inline-flex items-center gap-2 text-sm text-white/65 hover:text-white transition-colors" data-id="element-91" data-mp="335nmx9osjbm8">
                  <ArrowLeft className="h-4 w-4" data-id="element-92" data-mp="1htw1bmfw8w38" />
                  Cambiar mi respuesta
                </button>
              </motion.div>}

            {/* Question steps */}
            {!submitted && !disqualified && !isContactStep && <motion.div key={current.id} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-93" data-mp="2l3qmhfw7kzk4">
                {current.context && <p className="text-[15px] md:text-base text-white/90 font-normal leading-relaxed mb-5 pb-5 border-b border-white/10" data-id="element-94" data-mp="3huistltcbd8p">
                    {current.context}
                  </p>}
                <h3 className="text-xl md:text-[1.6rem] font-semibold text-white mb-2 leading-snug" data-id="element-95" data-mp="3ms14hjyqs6ke">
                  {current.title}
                </h3>
                {current.subtitle ? <p className="text-[15px] text-white/75 font-normal mb-7" data-id="element-96" data-mp="f79rl88mu1rh">
                    {current.subtitle}
                  </p> : <div className="mb-7" data-id="element-97" data-mp="9he0lmnbnfx" />}

                <div className="space-y-2.5" data-id="element-98" data-mp="58apk8idh4dg">
                  {current.options.map(option => {
                const selected = current.type === 'single' ? single[current.id] === option : (multi[current.id] ?? []).includes(option);
                return <button key={option} onClick={() => current.type === 'single' ? handleSingle(current.id, option) : toggleMulti(current.id, option)} className={`w-full flex items-center justify-between gap-4 text-left px-5 py-4 rounded-lg border transition-colors duration-150 ${selected ? 'border-gold bg-gold/10' : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]'}`} data-id="element-99" data-mp="3dqqbohg1ala3">
                        <span className="text-[15px] md:text-base text-white font-medium" data-id="element-100" data-mp="2mvvm0ftnqq0t">
                          {option}
                        </span>
                        <span className={`h-5 w-5 shrink-0 border flex items-center justify-center transition-colors duration-150 ${current.type === 'multi' ? 'rounded' : 'rounded-full'} ${selected ? 'border-gold bg-gold' : 'border-white/25'}`} data-id="element-101" data-mp="2dw08i4cmqixm">
                          {selected && <Check className="h-3 w-3 text-black" strokeWidth={3} data-id="element-102" data-mp="epl16s9bbp5r" />}
                        </span>
                      </button>;
              })}
                </div>

                <div className="mt-8 flex items-center justify-between gap-4" data-id="element-103" data-mp="3nw2ur1s85mew">
                  {step > 0 ? <button onClick={() => setStep(s => s - 1)} className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-white transition-colors" data-id="element-104" data-mp="3rxeggw7z9nht">
                      <ArrowLeft className="h-4 w-4" data-id="element-105" data-mp="5n21ui4qjuo9" />
                      Atrás
                    </button> : <span data-id="element-106" data-mp="1h420dvhof5qq" />}

                  {current.type === 'multi' && <button onClick={() => setStep(s => s + 1)} disabled={(multi[current.id] ?? []).length === 0} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold text-[15px] px-6 py-3 rounded-lg transition-colors duration-200 disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-gold" data-id="element-107" data-mp="rfwkuxzjfpcy">
                      Continuar
                      <ArrowRight className="h-4 w-4" data-id="element-108" data-mp="8nlsvz6f9btm" />
                    </button>}
                </div>
              </motion.div>}

            {/* Contact step */}
            {!submitted && isContactStep && <motion.div key="contact" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-109" data-mp="dnzy79gn27ny">
                <h3 className="text-xl md:text-[1.6rem] font-semibold text-white mb-4 leading-snug" data-id="element-110" data-mp="1mqkr44h1xd5v">
                  Tu información de contacto
                </h3>

                <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3.5 mb-8" data-id="element-111" data-mp="2fegnt96mf52n">
                  <Lock className="h-4 w-4 text-gold shrink-0 mt-0.5" strokeWidth={2} data-id="element-112" data-mp="2kc8pgux2i5k1" />
                  <p className="text-[13px] text-white/70 font-normal leading-relaxed" data-id="element-113" data-mp="26flfnrk0nkwp">
                    <span className="text-white/75 font-normal" data-id="element-114" data-mp="2adaupa91tfsl">
                      Tu información está segura.
                    </span>{' '}
                    Tus datos son privados y solo se usan para comunicarnos
                    contigo sobre tu plan para reparar tu crédito.
                  </p>
                </div>

                <form onSubmit={e => {
              e.preventDefault();
              if (contactValid && !sending) sendLead();
            }} className="space-y-5" data-id="element-115" data-mp="1looz8tzv3d6k">
                  <div data-id="element-116" data-mp="1cp28r38o7rsa">
                    <label htmlFor="qf-name" className={labelClass} data-id="element-117" data-mp="1cplelll1qppy">
                      Nombre y apellido
                    </label>
                    <input id="qf-name" type="text" autoComplete="name" required value={contact.name} onChange={e => setContact({
                  ...contact,
                  name: e.target.value
                })} placeholder="Eduardo Rodríguez" className={inputClass} data-id="element-118" data-mp="25cocqclt8yfo" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-119" data-mp="2vojf8idua9lm">
                    <div data-id="element-120" data-mp="30m1ns7ny2mt3">
                      <label htmlFor="qf-phone" className={labelClass} data-id="element-121" data-mp="30ncry8laxwkq">
                        Número de teléfono
                      </label>
                      <input id="qf-phone" type="tel" autoComplete="tel" required value={contact.phone} onChange={e => setContact({
                    ...contact,
                    phone: e.target.value
                  })} placeholder="+1 (407) 367-8672" className={inputClass} data-id="element-122" data-mp="39mg9h84tm3x4" />
                    </div>
                    <div data-id="element-123" data-mp="64pod7p0jpkz">
                      <label htmlFor="qf-email" className={labelClass} data-id="element-124" data-mp="660rr6ejqvbk">
                        Correo electrónico
                      </label>
                      <input id="qf-email" type="email" autoComplete="email" required value={contact.email} onChange={e => setContact({
                    ...contact,
                    email: e.target.value
                  })} placeholder="tucorreo@email.com" className={inputClass} data-id="element-125" data-mp="1c0wmsazn822n" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-126" data-mp="23rawdv0mis8i">
                    <div data-id="element-127" data-mp="28ryx58enmnfx">
                      <label htmlFor="qf-country" className={labelClass} data-id="element-128" data-mp="28si6w1u5i5iv">
                        ¿Dónde vives?
                      </label>
                      <select id="qf-country" required value={contact.country} onChange={e => setContact({
                    ...contact,
                    country: e.target.value,
                    city: ''
                  })} className={`${inputClass} appearance-none cursor-pointer`} data-id="element-129" data-mp="2do75o2ycjie4">
                        <option value="" data-id="element-130" data-mp="31m51k75azu37">Selecciona una opción</option>
                        <option value="Estados Unidos" data-id="element-131" data-mp="31o7sctea5r8z">Estados Unidos</option>
                        <option value="Puerto Rico" data-id="element-132" data-mp="2wtlnza6m8yye">Puerto Rico</option>
                      </select>
                    </div>
                    <div data-id="element-133" data-mp="2srqp0cc5qxns">
                      <label htmlFor="qf-city" className={labelClass} data-id="element-134" data-mp="3pbzhixkx5yro">
                        {contact.country === 'Estados Unidos' ? 'Estado' : 'Ciudad'}
                      </label>
                      <select id="qf-city" required disabled={cityOptions.length === 0} value={contact.city} onChange={e => setContact({
                    ...contact,
                    city: e.target.value
                  })} className={`${inputClass} appearance-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed`} data-id="element-135" data-mp="1inpb7aw0coyg">
                        <option value="" data-id="element-136" data-mp="pztieh9k7h6y">
                          {cityOptions.length === 0 ? 'Elige dónde vives primero' : 'Selecciona una opción'}
                        </option>
                        {cityOptions.map(city => <option key={city} value={city} data-id="element-137" data-mp="2pakvrl19mrqc">
                            {city}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div className="pt-3 flex items-center justify-between gap-4" data-id="element-138" data-mp="8n4hekoavkty">
                    <button type="button" onClick={() => setStep(s => s - 1)} className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-white transition-colors" data-id="element-139" data-mp="nfof35sol040">
                      <ArrowLeft className="h-4 w-4" data-id="element-140" data-mp="sdyb1im8c4qo" />
                      Atrás
                    </button>
                    <button type="submit" disabled={!contactValid || sending} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold text-[15px] px-7 py-3.5 rounded-lg transition-colors duration-200 disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-gold" data-id="element-141" data-mp="16bwq6q8dk578">
                      {sending ? <>
                          <Loader2 className="h-4 w-4 animate-spin" data-id="element-142" data-mp="39l86d1bfjgpu" />
                          Enviando...
                        </> : <>
                          Enviar y continuar
                          <ArrowRight className="h-4 w-4" data-id="element-143" data-mp="3jicu35kxbk8r" />
                        </>}
                    </button>
                  </div>
                </form>
              </motion.div>}

            {/* Success step */}
            {submitted && <motion.div key="result" initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.35,
            ease: [0.23, 1, 0.32, 1]
          }} className="text-center py-4" data-id="element-144" data-mp="2vttioh6kurcg">
                <motion.div initial={{
              scale: 0.6,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              type: 'spring',
              stiffness: 320,
              damping: 18,
              delay: 0.08
            }} className="h-16 w-16 rounded-full bg-emerald-500 ring-8 ring-emerald-500/15 flex items-center justify-center mx-auto mb-6" data-id="element-145" data-mp="p71w9wljmpcd">
                  <Check className="h-8 w-8 text-white" strokeWidth={3} data-id="element-146" data-mp="3tl8gydrqlqgt" />
                </motion.div>

                <h3 className="text-xl md:text-[1.6rem] font-semibold text-white mb-4 leading-snug" data-id="element-147" data-mp="3jpf38r2a7qxg">
                  ¡Felicidades, {contact.name.split(' ')[0]}! ¡Tu caso califica!
                </h3>
                <p className="text-base text-white/85 font-normal leading-relaxed mb-8 max-w-md mx-auto" data-id="element-148" data-mp="352pvk1xvm7fz">
                  Solo falta un paso: elige el día y la hora de tu diagnóstico
                  de 40 minutos. Revisamos tu reporte contigo y sales con un
                  plan concreto.
                </p>

                <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold px-8 py-4 rounded-lg transition-colors duration-200 text-base w-full sm:w-auto" data-id="element-149" data-mp="3033iptlymt7p">
                  Agenda tu llamada con nosotros
                  <ArrowRight className="h-5 w-5" data-id="element-150" data-mp="3j1phh1u0888g" />
                </a>

                <button onClick={resetForm} className="mt-8 inline-flex items-center gap-2 text-sm text-white/55 hover:text-white transition-colors" data-id="element-151" data-mp="ampdy5p86ihz">
                  <ArrowLeft className="h-4 w-4" data-id="element-152" data-mp="203jhvvdkveag" />
                  Volver a empezar
                </button>
              </motion.div>}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>;
}